import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PageTransitionWrapper from '../../components/layout/PageTransitionWrapper';
import { useFormulirList } from '../../components/hooks/useFormulir';

const ITEMS_PER_PAGE = 6; // ubah sesuai kebutuhan (kelipatan 3 agar grid rapi)

const COLORS = {
    green: { circle: 'bg-green-100', text: 'text-green-600', border: 'hover:border-green-600' },
    blue: { circle: 'bg-blue-100', text: 'text-blue-600', border: 'hover:border-blue-600' },
    red: { circle: 'bg-red-100', text: 'text-red-600', border: 'hover:border-red-600' },
    purple: { circle: 'bg-purple-100', text: 'text-purple-600', border: 'hover:border-purple-600' },
    amber: { circle: 'bg-yellow-100', text: 'text-yellow-600', border: 'hover:border-yellow-600' },
    indigo: { circle: 'bg-indigo-100', text: 'text-indigo-600', border: 'hover:border-indigo-600' },
};

// Ikon dipilih dari hash slug, jadi formulir yang sama selalu mendapat ikon yang sama
const ICON_PATHS = [
    'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
    'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
    'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z',
];

const pickIcon = (key = '') => {
    let h = 0;
    for (const ch of String(key)) h += ch.charCodeAt(0);
    return ICON_PATHS[h % ICON_PATHS.length];
};

const FormulirIcon = ({ seed, className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d={pickIcon(seed)} />
    </svg>
);

const FormulirCard = ({ item }) => {
    const c = COLORS[item.color] || COLORS.blue;
    return (
        <Link
            to={`/formulir/${item.slug}`}
            className={`bg-white p-6 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.03]
                        border-b-4 border-gray-200 ${c.border}
                        flex flex-col items-center text-center group`}
        >
            {/* Ikon */}
            <div className={`w-20 h-20 p-3 rounded-full mb-4 flex items-center justify-center overflow-hidden transition-all duration-300 ${c.circle} group-hover:shadow-lg group-hover:scale-110`}>
                {item.image ? (
                    <img src={item.image} alt={item.title} className="w-full h-full object-contain" />
                ) : (
                    <FormulirIcon seed={item.slug} className={`w-10 h-10 ${c.text}`} />
                )}
            </div>

            {/* Judul */}
            <h3 className="text-xl font-extrabold mb-1 uppercase leading-snug">{item.title}</h3>

            {/* Deskripsi */}
            <p className="text-sm text-gray-500 line-clamp-2">
                {item.description || 'Lihat persyaratan dan unduh formulir.'}
            </p>

            {/* Tombol aksi (dekorasi) */}
            <div className={`mt-4 text-sm font-semibold transition-colors duration-300 ${c.text} group-hover:underline`}>
                Lihat Formulir &rarr;
            </div>
        </Link>
    );
};

// Daftar nomor halaman dengan "..." bila terlalu banyak. Contoh: 1 ... 4 5 6 ... 12
const getPageNumbers = (current, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const pages = [1];
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);

    if (start > 2) pages.push('...');
    for (let p = start; p <= end; p++) pages.push(p);
    if (end < total - 1) pages.push('...');
    pages.push(total);

    return pages;
};

const Pagination = ({ currentPage, totalPages, onChange }) => {
    if (totalPages <= 1) return null;

    const baseBtn = 'min-w-[40px] h-10 px-3 rounded-lg text-sm font-semibold transition-colors duration-200';
    const navBtn = `${baseBtn} bg-gray-100 text-gray-700 hover:bg-blue-100 hover:text-blue-700 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-gray-100 disabled:hover:text-gray-700`;

    return (
        <nav className="mt-10 flex flex-wrap items-center justify-center gap-2" aria-label="Pagination">
            <button
                type="button"
                className={navBtn}
                onClick={() => onChange(currentPage - 1)}
                disabled={currentPage === 1}
            >
                &larr; Sebelumnya
            </button>

            {getPageNumbers(currentPage, totalPages).map((p, i) =>
                p === '...' ? (
                    <span key={`dots-${i}`} className="px-1 text-gray-400">...</span>
                ) : (
                    <button
                        key={p}
                        type="button"
                        onClick={() => onChange(p)}
                        aria-current={p === currentPage ? 'page' : undefined}
                        className={`${baseBtn} ${p === currentPage
                            ? 'bg-blue-600 text-white shadow-md'
                            : 'bg-gray-100 text-gray-700 hover:bg-blue-100 hover:text-blue-700'
                            }`}
                    >
                        {p}
                    </button>
                )
            )}

            <button
                type="button"
                className={navBtn}
                onClick={() => onChange(currentPage + 1)}
                disabled={currentPage === totalPages}
            >
                Selanjutnya &rarr;
            </button>
        </nav>
    );
};

const FormulirIndex = () => {
    const { list, loading, error } = useFormulirList();

    const [isLoading, setIsLoading] = useState(true);
    const [progress, setProgress] = useState(0);
    const [timeDone, setTimeDone] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    // Progress berbasis waktu, sama seperti halaman Absensi Pelkat
    useEffect(() => {
        let isCancelled = false;
        const MIN_LOADING_TIME = 1000;
        const startTime = Date.now();

        const progressInterval = setInterval(() => {
            const elapsed = Date.now() - startTime;
            let calculated = (elapsed / MIN_LOADING_TIME) * 100;
            if (calculated > 99) calculated = 99;
            if (!isCancelled) setProgress(Math.floor(calculated));
        }, 50);

        const timer = setTimeout(() => {
            clearInterval(progressInterval);
            if (!isCancelled) setTimeDone(true);
        }, MIN_LOADING_TIME);

        return () => {
            isCancelled = true;
            clearInterval(progressInterval);
            clearTimeout(timer);
        };
    }, []);

    // Selesai kalau waktu minimum lewat DAN data sudah datang
    useEffect(() => {
        if (!timeDone || loading) return;
        setProgress(100);
        const t = setTimeout(() => setIsLoading(false), 700);
        return () => clearTimeout(t);
    }, [timeDone, loading]);

    // Hitung pagination
    const totalPages = Math.max(1, Math.ceil(list.length / ITEMS_PER_PAGE));

    const pagedList = useMemo(() => {
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        return list.slice(start, start + ITEMS_PER_PAGE);
    }, [list, currentPage]);

    // Jaga-jaga kalau jumlah data berkurang dan halaman aktif melebihi total
    useEffect(() => {
        if (currentPage > totalPages) setCurrentPage(totalPages);
    }, [currentPage, totalPages]);

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <div className="bg-gray-50 min-h-screen py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">

                {/* Card 1: Judul dengan dekorasi (sama seperti Absensi) */}
                <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-10 relative overflow-hidden border-b-8 border-blue-500">
                    <div className="absolute top-0 right-0 h-full w-1/4 bg-blue-50 opacity-50 transform skew-x-12 origin-top-right hidden sm:block"></div>
                    <div className="relative z-10">
                        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-gray-900 text-center mb-2">
                            FORMULIR & PERSYARATAN
                        </h1>
                        <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto font-medium">
                            Pilih formulir di bawah ini untuk melihat persyaratan, mengunduh, atau mencetak formulir pendaftaran.
                        </p>
                    </div>
                </div>

                {/* Card 2: Daftar formulir */}
                <div className="mt-10 bg-white rounded-2xl shadow-xl p-6 sm:p-10 min-h-[500px] relative">
                    {isLoading ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <LoadingSpinner progress={progress} />
                        </div>
                    ) : error ? (
                        <p className="text-center text-red-600 py-16">Gagal memuat data.</p>
                    ) : list.length === 0 ? (
                        <p className="text-center text-gray-500 py-16">Belum ada formulir.</p>
                    ) : (
                        <PageTransitionWrapper key={currentPage}>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                                {pagedList.map((item) => (
                                    <FormulirCard key={item.id} item={item} />
                                ))}
                            </div>

                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onChange={handlePageChange}
                            />

                            <p className="mt-4 text-center text-sm text-gray-400">
                                Menampilkan {(currentPage - 1) * ITEMS_PER_PAGE + 1}
                                {' - '}
                                {Math.min(currentPage * ITEMS_PER_PAGE, list.length)} dari {list.length} formulir
                            </p>
                        </PageTransitionWrapper>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FormulirIndex;